/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import pick from 'lodash/pick';

import config from '../../config';
import contentTaskResource from '../content/task/content-task-resource';
import layerResource from '../layer/layer-resource';
import layoutResource from '../layout/layout-resource';
import mediumResource from '../medium/medium-resource';
import playlistResource from '../playlist/playlist-resource';
import powerTaskResource from '../power/power-task-resource';
import PromiseUtil from '../promise/promise-util';
import { emptySessionAttrs } from '../session/session-attrs';
import taskResource from '../task/task-resource';
import timerResource from '../timer/timer-resource';
import trackResource from '../track/track-resource';

/**
 * @private
 * @typedef {import('../session/legacy-session-attrs').SessionOrLegacyAttrs} SessionOrLegacyAttrs
 * @typedef {import('../session/session-attrs').SessionAttrs} SessionAttrs
 * @typedef {import('./store').default} Store
 */

/**
 * Namigruje data v localStorage v legacy formátu (před #409, tj. perzistentní
 * a tzv. falešně perzistentní, která ale šla odvodit z ContentSettings JSON)
 * na nový (persistent-only) data.
 *
 * Funkce je idempotentní a může se opakovaně volat na legacy i novém formátu.
 * Teoreticky je možné i po migraci na nový formát downgradovat verzi, kde by
 * díky nepřítomnosti položky `contentTimestamp` v localStorage v novém formátu
 * mělo dojít po prvním heartbeatu (po nerovnosti hodnot) k automatickému
 * dostažení obsahu.
 * @param {Store} store
 * @returns {Promise<void>}
 */
export function migrate(store) {
  // TODO: Ošetřovat nenalezení Session nebo nechat probublat výjimku?
  // Funkce volána až je zajištěna existence session.
  return store
    .findSession(config.device.session.defaultId)
    .then((/** @type {SessionOrLegacyAttrs | undefined} */ session) => {
      if (isNil(session) || isNil(session.contentTimestamp)) {
        return;
      }
      // Mazání/migraci chceme udělat jen při prvním úspěšném zavolání funkce.
      // Pokud spadne mazání obsahu, tak se stále zachová contentTimestamp v Session,
      // takže je šance na druhý/další pokus po spuštění aplikace.
      return PromiseUtil.allSerial([
        // Mazání objektů z localStorage, které se nově používají jako in-memory.
        () => destroyContent(store),
        // Pročištění uloženého Session od odvoditelných položek.
        () => migrateSession(store, session)
      ]).then(noop);
    });
}

// TODO #409: Po zrušení JSData z parserů (komunikátorů) refaktorovat na hledání
// a mazání prefixu localStorage klíčů všech resourců kromě stále perzistentních
// a klíčů `player-lighter/session` a `player-lighter/session/default`.
/**
 * Provede smazání objektů, které není potřebe ukládat perzistentně
 * a je používána pouze jejich in-memory varianta.
 * @private
 * @param {Store} store
 * @returns {Promise<void>}
 */
function destroyContent(store) {
  return PromiseUtil.allSerial(
    [
      contentTaskResource.name,
      layerResource.name,
      layoutResource.name,
      mediumResource.name,
      playlistResource.name,
      powerTaskResource.name,
      taskResource.name,
      timerResource.name,
      trackResource.name
    ].map(
      (resourceName) => () =>
        // @ts-expect-error Mažou se i nyní neperzistentní věci.
        store.destroyAll(resourceName)
    )
  ).then(noop);
}

/**
 * Provede přepsání záznamů v Session jen za ty položky,
 * které je nutné ukládat perzistentně.
 * @private
 * @param {Store} store
 * @param {SessionOrLegacyAttrs} sessionOrLegacyAttrs
 * @returns {Promise<void>}
 */
function migrateSession(store, sessionOrLegacyAttrs) {
  // Zachování jen známých klíčů. Předpokládá, že defaultizace se již
  // provedla pomocí Session.findDefaultMigratedOrCreate.
  // Po migraci zmizí klíč contentTimestamp, což je pak zarážka
  // pro opětovné nespouštění celé migrace.
  const sessionAttrs = /** @type {SessionAttrs} */ (
    /** @type {unknown} */ (
      pick(sessionOrLegacyAttrs, Object.keys(emptySessionAttrs))
    )
  );
  return store
    .createSession(sessionAttrs, {
      // Přepsat celý existující záznam.
      onConflict: 'replace',
      // Pokud již záznam existuje, aktualizuje se.
      upsert: true
    })
    .then(noop);
}
